import React from 'react';
import { Badge } from '@mantine/core';
import { Calendar } from 'lucide-react';
import { CustomDatePicker } from './CustomDatePicker';
import { isOverdue, isDueSoon, formatCompactDate } from '../utils/dateUtils';
// CSS import removed to avoid conflicts with Mantine styling

interface InlineDatePickerProps {
  value?: string;
  onChange: (date: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  value,
  onChange,
  placeholder = "Due Date"
}) => {
  const handleDateChange = (dateString: string) => {
    if (dateString) {
      // Convert YYYY-MM-DD format to ISO string for storage
      const [year, month, day] = dateString.split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // month is 0-based
      const isoString = localDate.toISOString();
      onChange(isoString);
    } else {
      onChange(undefined);
    }
  };

  // Convert ISO string to YYYY-MM-DD format for CustomDatePicker
  const getDatePickerValue = () => {
    if (!value) return '';
    try {
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  // Get urgency styling for Mantine Badge
  const getUrgencyColor = () => {
    if (!value) return 'gray';
    if (isOverdue(value)) return 'red';
    if (isDueSoon(value)) return 'orange';
    return 'blue';
  };

  const getUrgencyVariant = () => {
    if (!value) return 'light';
    if (isOverdue(value)) return 'filled';
    if (isDueSoon(value)) return 'light';
    return 'light';
  };

  const formatDisplayDate = (dateValue: string) => {
    try {
      return formatCompactDate(dateValue);
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <CustomDatePicker
      value={getDatePickerValue()}
      onChange={handleDateChange}
      placeholder={placeholder}
      displayFormat="inline"
      showIcon={true}
      showSnooze={true}
      renderTrigger={(onClick) => (
        <Badge
          variant={getUrgencyVariant()}
          color={getUrgencyColor()}
          leftSection={<Calendar size={12} />}
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          {value ? formatDisplayDate(value) : placeholder}
        </Badge>
      )}
    />
  );
};
